import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetOrganizationByIDQueryKey,
  useUpdateOrganizationByID,
} from '@/api/trafikportalen';
import { toast } from '@/hooks/useToast';
import { IconCheck } from '@/icons';
import { checkUserPermissions } from '../Auth/checkUserPermissions';
import { FormSection } from '../FormSection';
import Button from '../core/Button';
import CheckBox from '../core/Checkbox';
import type { OrganizationDTO } from '@/api/trafikportalen';

interface OrganizationPeriodAccessProps {
  organization: OrganizationDTO;
}

export const OrganizationPeriodAccess = ({
  organization,
}: OrganizationPeriodAccessProps) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const updateOrganization = useUpdateOrganizationByID();
  const selfManaged = organization.selfmanaged_school_claims === true;
  const [editMode, setEditMode] = useState(false);
  const [checked, setChecked] = useState(selfManaged);

  const startEdit = useCallback(() => {
    setChecked(selfManaged);
    setEditMode(true);
  }, [selfManaged]);

  const cancelEdit = useCallback(() => {
    setEditMode(false);
  }, []);

  const save = useCallback(async () => {
    try {
      await updateOrganization.mutateAsync({
        organizationID: organization.id,
        data: {
          name: organization.name,
          organization_number: organization.organization_number,
          state: organization.state,
          credit: organization.credit,
          discount_codes: organization.discount_codes ?? {},
          enabled_collection_types: organization.enabled_collection_types ?? [],
          selfmanaged_school_claims: checked,
        },
      });
      await queryClient.invalidateQueries({
        queryKey: getGetOrganizationByIDQueryKey(organization.id),
      });
      setEditMode(false);
      toast.success(
        {
          title: formatMessage({ defaultMessage: 'Inställningarna sparades' }),
        },
        { autoClose: 3000 },
      );
    } catch {
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Kunde inte spara inställningarna',
          }),
        },
        { autoClose: 3000 },
      );
    }
  }, [checked, formatMessage, organization, queryClient, updateOrganization]);

  const editButtons = (
    <div className="flex gap-2">
      <Button
        variant="ghost"
        onClick={cancelEdit}
        disabled={updateOrganization.isPending}
      >
        <FormattedMessage defaultMessage="Avbryt" />
      </Button>
      <Button
        variant="filled"
        onClick={save}
        loading={updateOrganization.isPending}
        disabled={checked === selfManaged}
        startIcon={<IconCheck />}
      >
        <FormattedMessage defaultMessage="Spara" />
      </Button>
    </div>
  );

  return (
    <FormSection
      title={<FormattedMessage defaultMessage="Periodåtkomst" />}
      editMode={editMode}
      onClickButton={startEdit}
      canSeeButton={checkUserPermissions({ permission: 'tenant:editor' })}
      editActions={editButtons}
    >
      <CheckBox
        id="selfmanaged_school_claims"
        checked={editMode ? checked : selfManaged}
        onCheckedChange={(value) => setChecked(value === true)}
        disabled={!editMode}
        label={formatMessage({
          defaultMessage: 'Organisationen får hantera sina egna perioder',
        })}
      />
      <p className="text-text-800">
        <FormattedMessage defaultMessage="Om organisationen inte har periodåtkomst kan dess administratörer se perioderna för aktiverade elevkategorier, men inte skapa, ändra eller ta bort dem." />
      </p>
    </FormSection>
  );
};
